import React, { useState } from "react";
import { Modal, TextArea, Font } from "pepsico-ds";
import styled from "styled-components";
import { getBoxColor, getfeedback } from "../../utils/fieldConstants";
import { sendAppFeedBack } from "../../redux/reducers/appData/appFeedbackReducer";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { CloseOutRadioActions } from "../../redux/closeoutslice/closeOutRadioSlice";
import { getUserAccess } from "../../redux/actions/users/userAccessAction";

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
`;

const RatingsBoxContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  position: relative;
  margin: 0 auto 32px auto;
`;

const RatingBox = styled.div<{ isSelected: boolean; color: string }>`
  cursor: pointer;
  width: 0px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ color }) => color};
  border: ${({ isSelected }) =>
    isSelected ? "1px solid #000" : "1px solid #fffff"};
  color: white;
  font-weight: bold;
  transition: transform 0.2s, border 0.2s;
  flex: 1;

  &:hover {
    transform: scale(1.05);
  }
`;

const IconContainer = styled.div`
  position: absolute;
  bottom: -28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
`;

const LeftIcon = styled(IconContainer)`
  left: 0%;
`;

const RightIcon = styled(IconContainer)`
  right: 0%;
`;

interface SmileyRatingsProps {
  isModalOpen: boolean;
  onModalClose: () => void;
}

const SmileyRatings: React.FC<SmileyRatingsProps> = ({
  isModalOpen,
  onModalClose,
}) => {
  const [selectedRating, setSelectedRating] = useState<number | null>(null);
  const [primaryReason, setPrimaryReason] = useState("");
  const [improvementSuggestion, setImprovementSuggestion] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const handleRatingSelect = (rating: number) => {
    setSelectedRating(rating);
  };

  const handleCloseModal = () => {
    if (onModalClose) {
      onModalClose();
    }
    setSelectedRating(null);
    setPrimaryReason("");
    setImprovementSuggestion("");
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await dispatch(
        sendAppFeedBack({
          rating: selectedRating || 0,
          feedback: getfeedback(selectedRating || 0),
          reason: primaryReason,
          improvements: improvementSuggestion,
        })
      );
      setIsSubmitting(false);
      dispatch(getUserAccess());
      localStorage.setItem("isSurveySubmitted", "true");
      handleCloseModal();
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          message: "Feedback for the app usage submitted successfully.",
          severity: "success",
        })
      );
    } catch (error) {
      dispatch(
        CloseOutRadioActions.setSnackBarMessage({
          message:
            "Facing issue while submitting feedback. please try again later.",
          severity: "error",
        })
      );
      console.error("Error submitting feedback:", error);
    }
  };

  return (
    <Modal
      showModal={isModalOpen}
      closeOnOutsideClick={false}
      onCloseModal={handleCloseModal}
      title={"Feedback"}
      hideCloseButton={true}
      text={
        <>
          <QuestionContainer>
            <Font className="$font-xsmall-regular">
              How likely are you to recommend our digital platform to a friend
              or colleague? (1-10 scale) *
            </Font>
            <div style={{ marginLeft: "0%", marginTop: "5px", width: "100%" }}>
              <RatingsBoxContainer>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <RatingBox
                    isSelected={selectedRating === rating}
                    onClick={() => handleRatingSelect(rating)}
                    color={getBoxColor(rating)}
                  >
                    {rating}
                  </RatingBox>
                ))}
                <LeftIcon>
                  😠{" "}
                  <Font className="$font-xsmall-regular">
                    Not at all likely
                  </Font>
                </LeftIcon>
                <RightIcon>
                  🙂{" "}
                  <Font className="$font-xsmall-regular">Extremely likely</Font>
                </RightIcon>
              </RatingsBoxContainer>
            </div>
          </QuestionContainer>
          <div style={{ marginBottom: "20px" }}>
            <TextArea
              className="primary-reason-textarea"
              label=" What is the primary reason for your score?"
              placeholder="Please share your thoughts..."
              value={primaryReason}
              onUpdate={(value) => {
                setPrimaryReason(value);
              }}
              showCharacterCount={false}
              showToolbar={false}
              maxLength={1000}
              style={{ resize: "none", overflow: "auto" }}
            />
          </div>
          <TextArea
            className="primary-reason-textarea"
            label="What can we do to improve your experience? *"
            placeholder="Your suggestions are valuable to us..."
            value={improvementSuggestion}
            onUpdate={(value) => {
              setImprovementSuggestion(value);
            }}
            showCharacterCount={false}
            showToolbar={false}
            maxLength={1000}
            style={{ resize: "none", overflow: "auto" }}
          />
        </>
      }
      primaryButtonProps={{
        text: isSubmitting ? "Submitting..." : "Submit Feedback",
        onClick: handleSubmit,
        disabled:
          isSubmitting ||
          selectedRating === null ||
          !improvementSuggestion.trim(),
        size: "medium",
        variant: "primary",
      }}
      showAuxiliaryButton={false}
      showTertiaryButton={false}
      showSecondaryButton={true}
      secondaryButtonProps={{
        text: "Remind Me Later",
        onClick: handleCloseModal,
        size: "medium",
        variant: "secondary",
      }}
      showLink={false}
      style={{
        display: "flex",
        flexDirection: "column",
        overflow: "scroll",
        maxHeight: "80vh",
      }}
    />
  );
};

export default SmileyRatings;
